import {ChangeDetectionStrategy, Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild} from '@angular/core';
import {Chart, ChartArea, ChartConfiguration, ChartData, ChartDataset, ChartOptions, Point, PointStyle} from 'chart.js';
import {Tile, TILE_TYPE_SIMPLE_ANALOG, TILE_TYPE_SIMPLE_BOOL, TILE_TYPE_SIMPLE_MULTISTATE, TileStateMap} from '../../../shared/models/tile';
import {TileDataset} from '../../../shared/models/tile-dataset';
import {BaseChartDirective} from 'ng2-charts';
import {BehaviorSubject, Subscription} from 'rxjs';
import {TileValue} from '../../../shared/models/tile-value';
import {FaIconComponent} from '../fa-icon/fa-icon.component';
import momentTimezone from 'moment-timezone/builds/moment-timezone-with-data-10-year-range';

Chart.defaults.font.family = '"Inter", "Roboto Light", Roboto, sans-serif';
Chart.defaults.color = '#111';
Chart.defaults.hover.axis = 'x';
// Chart.defaults.plugins.legend.onClick = () => {
// };

/**
 * This is especially useful for any situation where an integer value maps to a string
 * state--usually multistate tile situations. Sometimes the integers that can be values
 * for a tile don't follow array index order (0, 1, 2...) but instead skip array index
 * numbers (for example: 1, 2, 4, 10, 11, 12) but Chart.js only takes an array, not an
 * object with integer keys. So "flattened" labels will be assigned new indexes that
 * start at 0 and increment by 1, while also tracking the original value the new value
 * represents.
 */
type FlattenedLabels = {
  originalValue: number;
  flattenedValue: number;
  labelText: string;
}[];

@Component({
  selector: 'motn-tile-history',
  templateUrl: './tile-history.component.html',
  styleUrls: ['./tile-history.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    BaseChartDirective,
    FaIconComponent,
  ],
})
export class TileHistoryComponent implements OnInit, OnChanges, OnDestroy {
  @Input() datasets$: BehaviorSubject<TileDataset[]>;
  @Input() timezone: string = Intl.DateTimeFormat().resolvedOptions().timeZone;

  private datasetsSub: Subscription;

  private rgbSets: Array<number[]> = [
    [94, 135, 231], // purple
    [56, 184, 186], // turquoise
    [214, 118, 71], // orange
    [141, 176, 59], // green
    [231, 94, 134], // pinkish
  ];
  private shapeSets: PointStyle[] = ['circle', 'triangle', 'cross', 'rect', 'star'];
  private flattenedLabels: FlattenedLabels = []; // for non-analog data types
  private sampleTile: Tile;

  datasets: TileDataset[] = [];

  /**
   * This object is supplied for the initial render; subsequent
   * updates are made directly to this.chart.data.datasets
   */
  initialChartData: ChartData<'line'> = {datasets: []};
  chartOptions: ChartOptions<'line'> = {};

  @ViewChild(BaseChartDirective) chart: BaseChartDirective;


  constructor() {
  }


  ngOnInit(): void {
    this.datasetsSub = this.datasets$.subscribe(
      (data) => {
        if (data?.length) {
          this.datasets = data;
          this.sampleTile = this.datasets[0].tile;
          this.onDatasetsReceived();
        }
      },
    );
  }


  ngOnChanges(changes: SimpleChanges): void {
    if (changes['timezone'] && !changes['timezone'].firstChange) {
      this.chart?.update();
    }
  }

  ngOnDestroy() {
    this.datasetsSub?.unsubscribe();
  }


  /**
   *
   * @private
   */
  private onDatasetsReceived() {
    const firstDatasetTile = this.getSampleTile();

    if (firstDatasetTile) {
      this.chartOptions = {...this.chartOptionsTemplate};

      switch (firstDatasetTile.Type) {
        case TILE_TYPE_SIMPLE_ANALOG:
          // nothing
          break;
        case TILE_TYPE_SIMPLE_BOOL:
        case TILE_TYPE_SIMPLE_MULTISTATE:
          const paddingOptions = this.chartOptions.layout.padding as ChartArea;
          paddingOptions.top = 20;

          this.makeFlattenedLabels(firstDatasetTile.getStateLabels());
          this.flattenDatasetValues();

          // @ts-ignore
          this.chartOptions.scales.y.labels = this.getAxisLabels();
          this.chartOptions.scales.y.type = 'category';

          break;
      }
    }

    this.prepareAndRenderChartData(); // chart renders at end of this call
  }


  /**
   * This returns the tile belonging to the first known dataset. This is useful for
   * determining tile type and for getting tile state labels for tile types of which
   * this component can only render one at a time.
   *
   * If there are no datasets stored in this.datasets, will return null.
   */
  private getSampleTile(): Tile {
    const datasetQty: number = this.datasets.length;

    if (datasetQty) {
      // get a sample tile from dataset to determine chart type; more than one type should
      // not be present, even if there are multiple tiles
      let ds: TileDataset = this.datasets[0];
      return ds.tile;
    }

    return null;
  }


  /**
   * @see FlattenedLabels
   * @param labelMap
   * @private
   */
  private makeFlattenedLabels(labelMap: TileStateMap): FlattenedLabels {
    const flattenedLabels: FlattenedLabels = [];

    let index: number = 0;

    for (let key in labelMap) {
      flattenedLabels.push({
        originalValue: +key,
        flattenedValue: index++,
        labelText: labelMap[key],
      });
    }

    this.flattenedLabels = flattenedLabels;
    return this.flattenedLabels;
  }


  /**
   * Assumes that makeFlattenedLabels has been run first, if relevant.
   *
   * Applies flattened label values directly to this.datasets.
   *
   * A "labeled" value is one that is numeric behind the scenes but for which
   * a string of text is used to convey state--all boolean and multistate values
   * are such.
   *
   * @see makeFlattenedLabels
   * @see datasets
   * @private
   */
  private flattenDatasetValues() {
    const flattenedLabelMap: { [originalVal: number]: number } = {};

    this.flattenedLabels.map((f) => {
      flattenedLabelMap[f.originalValue] = f.flattenedValue;
    });

    for (let i1 in this.datasets) {
      let ds = this.datasets[i1];

      for (let i2 in ds.data) {
        let val = ds.data[i2].Value;
        ds.data[i2].Value = flattenedLabelMap[+val];
      }
    }
  }


  /**
   * For converting Motion TileValue objects into Chartjs ChartPoint objects
   * @param tileValue
   */
  private makeChartPointObject(tileValue: TileValue): Point {
    let value;
    const tile = this.sampleTile;

    if (tile.Type === TILE_TYPE_SIMPLE_ANALOG) {
      value = (tileValue.Value as number).toFixed(this.getDecimals());
    } else {
      value = this.flattenedLabels.find(fl => fl.flattenedValue === tileValue.Value).labelText;
    }

    const point = {
      x: tileValue.Time as unknown as number,
      y: value,
    };

    return point;
  }


  /**
   * Inserts new data into existing chart datasets and/or creates brand new chart
   * datasets when there is no existing one.
   */
  private prepareAndRenderChartData() {
    const chartDataSets: ChartDataset<'line'>[] = [];
    const datasetsArr: TileDataset[] = this.datasets;

    if (this.chart && datasetsArr.length === this.chart.data.datasets.length) {
      for (let i in datasetsArr) {
        const ds = this.datasets[i];
        const chartPoints: Point[] = ds.data.filter(
          val => val.Value !== null,
        ).map(
          val => this.makeChartPointObject(val),
        );
        this.chart.data.datasets[i].data = chartPoints;
      }
    } else {
      for (let i in datasetsArr) {
        const ds = this.datasets[i];
        const chartPoints: Point[] = ds.data.filter(
          val => val.Value !== null,
        ).map(
          val => this.makeChartPointObject(val),
        );

        let chartDataSet: ChartDataset<'line'> = {
          data: chartPoints,
          borderColor: 'rgba(94, 135, 231, 0.5)',
          pointRadius: 2,
          borderWidth: 2,
          backgroundColor: 'rgba(0, 0, 0, 0)',
          pointBackgroundColor: '#fff',
          pointBorderColor: 'rgba(94, 135, 231, 1)',
          pointHoverBorderColor: 'rgba(94, 135, 231, 1)',
          pointHoverBorderWidth: 3,
          pointHoverRadius: 3,
          pointHitRadius: 4,
          pointStyle: 'circle',
          stepped: this.getSampleTile().Type !== TILE_TYPE_SIMPLE_ANALOG,

        };

        chartDataSets.push(chartDataSet);
      }

      this.colorizeDataSets(chartDataSets);
      this.initialChartData.datasets = chartDataSets;
    }
  }


  /**
   * Edits chart line and point colors in place. Designed to ensure that when
   * multiple tiles' data is shown simultaneously, there are different colors
   * for each one.
   *
   * @param chartDataSets
   */
  private colorizeDataSets(chartDataSets: ChartDataset<'line'>[]): void {
    for (let i in chartDataSets) {
      let rgbIndex = this.colorIndexFor(+i);
      let ds = chartDataSets[i];

      ds.borderColor = this.getRgbColorString(rgbIndex, 0.5);
      ds.pointBorderColor = this.getRgbColorString(rgbIndex);
      ds.hoverBorderColor = this.getRgbColorString(rgbIndex);
      ds.backgroundColor = this.getRgbColorString(rgbIndex);
      ds.hoverBackgroundColor = this.getRgbColorString(rgbIndex);
      ds.pointStyle = this.shapeSets[i];
    }
  }


  /**
   * Assumes that makeFlattenedLabels has been run first, if relevant.
   *
   * @see makeFlattenedLabels
   * @see flattenDatasetValues
   * @private
   */
  private getAxisLabels(): string[] {
    return this.flattenedLabels.map((f) => {
      return f.labelText;
    });
  }


  private getRgbColorString(index: number, opacity: number = 1): string {
    let rgbSet = this.rgbSets[index];
    return `rgba(${rgbSet[0]}, ${rgbSet[1]}, ${rgbSet[2]}, ${opacity})`;
  }


  private colorIndexFor(i: number): number {
    return i % this.rgbSets.length;
  }


  /**
   * For numeric charts that support stacking, return the highest number
   * of decimal places configured to be shown out of all shown tiles.
   *
   * @private
   */
  private getDecimals(): number {
    const tiles = this.getTiles();

    let decimals: number = 0;

    for (let tile of tiles) {
      let _d = tile.Appearance?.Config?.Decimal;

      if (_d && _d > decimals) {
        decimals = _d;
      }
    }

    return decimals;
  }


  /**
   * Returns all the Tile objects being used by the chart currently
   */
  private getTiles(): Tile[] {
    return this.datasets.map(
      ds => ds.tile,
    );
  }

  get legendItems(): { tile: Tile; color: string }[] {
    return this.datasets.map((ds, i) => ({
      tile: ds.tile,
      color: this.getRgbColorString(this.colorIndexFor(i)),
    }));
  }


  /**
   * This is a template object... it gets modified during init above before being
   * supplied to chartjs
   */
  readonly chartOptionsTemplate: ChartConfiguration<'line'>['options'] = {
    devicePixelRatio: 4,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        mode: 'point',
        intersect: false,
        callbacks: {
          label: (tooltipItem) => {
            let tile: Tile = this.datasets[tooltipItem.datasetIndex].tile;
            let units = tile.getUnitsLabel();
            const time = momentTimezone.tz(tooltipItem.parsed.x, this.timezone).format('MMM D, YYYY h:mm:ss A z');

            if (units !== null) {
              return `${tile.Name} — ${time} — ${tooltipItem.formattedValue} ${units}`;
            } else {
              return `${tile.Name} — ${time} — ${tooltipItem.formattedValue}`;
            }
          },
        },
        cornerRadius: 10,
        backgroundColor: '#fff',
        borderColor: '#ccc',
        bodyColor: '#67748e',
        borderWidth: 0.5,
        titleFont: {
          size: 0,
        },
        bodyFont: {
          family: 'Manrope, sans-serif',
          weight: 500,
        },
        displayColors: false,
      },
    },
    layout: {
      padding: {
        left: 10,
        right: 10,
      },
    },
    maintainAspectRatio: false,
    responsive: true,
    animation: {
      duration: 0,
    },
    scales: {
      x: {
        type: 'time',
        border: {
          display: false,
          dash: [1, 2]
        },
        grid: {
          color: '#ececec',
          lineWidth: 0.5,
          drawTicks: false,
        },
        ticks: {
          padding: 10,
          callback: (value: number | string, index: number, ticks: any[]) => {
            const ts = ticks[index]?.value;
            if (ts == null) return String(value);
            const interval = ticks.length > 1 ? ticks[1]?.value - ticks[0]?.value : 0;
            const MIN = 60 * 1000;
            const HOUR = 60 * MIN;
            const DAY = 24 * HOUR;
            const MONTH = 30 * DAY;
            let fmt: string;
            if (interval < MIN)        fmt = 'h:mm:ss a';
            else if (interval < HOUR)  fmt = 'h:mm a';
            else if (interval < DAY)   fmt = 'h a';
            else if (interval < MONTH) fmt = 'MMM D';
            else                       fmt = 'MMM YYYY';
            return momentTimezone.tz(ts, this.timezone).format(fmt);
          },
        },
      },
      y: {
        type: 'linear',
        border: {
          display: false,
        },
        grid: {
          color: '#ececec',
          lineWidth: 0.5,
          drawTicks: false,
          display: false,
        },
        ticks: {
          padding: 10,
        },
      },
    },
  };

}
